import type { QueryInput, SortInput } from "@/services/entity-instance.service";
import type { MediaUsageRelation } from "@/services/pylo-media.service";

export type MediaKind = "image" | "video" | "audio" | "document" | "other";

export const MEDIA_KINDS: Array<MediaKind> = ["image", "video", "audio", "document", "other"];

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  image: "Images",
  video: "Video",
  audio: "Audio",
  document: "Documents",
  other: "Other",
};

export type MediaSort = "newest" | "oldest" | "name" | "largest" | "smallest";

export const MEDIA_SORT_LABELS: Record<MediaSort, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  name: "File name",
  largest: "Largest first",
  smallest: "Smallest first",
};

export interface UsedByFilter {
  /** Same discriminator as `MediaUsageRelation.source`. */
  source: "inverse" | "owner";
  /**
   * Inverse: field on `PyloMedia`. Owner: field on `entityName` pointing to
   * `PyloMedia`. Not unique across sources on its own, so pair it with
   * `entityName` when identifying the relation.
   */
  fieldName: string;
  /** Owning entity of the relation. */
  entityName: string;
  /** Narrow to one record; without it the filter means "used by any of these". */
  instanceId?: string;
  instanceLabel?: string;
}

export interface MediaLibraryFilter {
  search: string;
  kind?: MediaKind;
  usedBy?: UsedByFilter;
  sort: MediaSort;
  /**
   * Hard constraint from a relation's `media_allowed_computed_mime_types`, not a
   * user choice — the backend rejects the connect otherwise
   * (`PyloMedia::validatePyloRelation`), so an unpickable file must never be offered.
   */
  lockedMimeTypes?: Array<string>;
}

export const EMPTY_MEDIA_FILTER: MediaLibraryFilter = {
  search: "",
  sort: "newest",
};

const mimeLike = (pattern: string, negate = false): QueryInput => ({
  condition: {
    field: "file_mime_type",
    operator: negate ? "notLike" : "like",
    value: pattern,
  },
});

const KIND_PREFIXES = ["image/%", "video/%", "audio/%", "application/%", "text/%"];

/**
 * Mime type is nullable, and SQL `NOT LIKE` drops NULLs — a file with no mime
 * type would vanish from "Other" instead of landing in it, so it's matched
 * explicitly.
 */
const kindCondition = (kind: MediaKind): QueryInput => {
  switch (kind) {
    case "image":
      return mimeLike("image/%");
    case "video":
      return mimeLike("video/%");
    case "audio":
      return mimeLike("audio/%");
    case "document":
      return { or: [mimeLike("application/%"), mimeLike("text/%")] };
    case "other":
      return {
        or: [
          { and: KIND_PREFIXES.map((prefix) => mimeLike(prefix, true)) },
          { condition: { field: "file_mime_type", operator: "isNull" } },
        ],
      };
  }
};

const SORTS: Record<MediaSort, SortInput> = {
  newest: { field: "created_at", order: "desc" },
  oldest: { field: "created_at", order: "asc" },
  name: { field: "file_name", order: "asc" },
  largest: { field: "file_size_bytes", order: "desc" },
  smallest: { field: "file_size_bytes", order: "asc" },
};

/**
 * Translate the library's filter state into the canonical pylo query tree.
 *
 * Inverse-source `usedBy` traverses `PyloMedia.<field>.id` directly. Owner-
 * source has no inverse to walk, so the caller must pre-resolve the referenced
 * media IDs (see `useUsedByOwnerMediaIds`) and pass them via
 * `ownerUsedByMediaIds` — this function then emits an `id IN (…)` clause. If
 * the resolution is still pending, pass `undefined` and skip the query.
 */
export const buildMediaQuery = (
  filter: MediaLibraryFilter,
  _relations: Array<MediaUsageRelation>,
  ownerUsedByMediaIds?: Array<string>,
): Array<QueryInput> => {
  const query: Array<QueryInput> = [];

  const search = filter.search.trim();
  if (search) {
    query.push({
      condition: { field: "file_name", operator: "ilike", value: `%${search}%` },
    });
  }

  if (filter.lockedMimeTypes && filter.lockedMimeTypes.length > 0) {
    query.push({
      condition: { field: "file_mime_type", operator: "in", values: filter.lockedMimeTypes },
    });
  }

  if (filter.kind) {
    query.push(kindCondition(filter.kind));
  }

  if (filter.usedBy) {
    if (filter.usedBy.source === "inverse") {
      const field = `${filter.usedBy.fieldName}.id`;
      query.push(
        filter.usedBy.instanceId
          ? { condition: { field, operator: "equal", value: filter.usedBy.instanceId } }
          : { condition: { field, operator: "isNotEmpty" } },
      );
    } else if (ownerUsedByMediaIds) {
      // An empty pre-resolution means "no media matches" — emit an unsatisfiable
      // clause so the list stays empty instead of silently dropping the filter.
      query.push(
        ownerUsedByMediaIds.length > 0
          ? { condition: { field: "id", operator: "in", values: ownerUsedByMediaIds } }
          : { condition: { field: "id", operator: "equal", value: "__no_match__" } },
      );
    }
  }

  return query;
};

export const buildMediaSort = (filter: MediaLibraryFilter): Array<SortInput> => [
  SORTS[filter.sort],
];

export const isFilterActive = (filter: MediaLibraryFilter): boolean =>
  filter.search.trim().length > 0 || !!filter.kind || !!filter.usedBy;
